"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  planSeeding,
  type SeedError,
  type SeedInput,
  type SeedMode,
  type SeedPlan,
  VESSELS,
  type Vessel,
  type VesselId,
  vesselById,
} from "@/lib/tool-kit/biology-lab/cell-seeding";
import {
  type NumberFormatter,
  plain,
  sig,
  toTsv,
} from "@/lib/tool-kit/biology-lab/format";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const CELL_SEEDING_CALCULATOR_STRINGS = {
  vesselLabel: "Culture vessel",
  vesselDish35: "35 mm dish",
  vesselDish60: "60 mm dish",
  vesselDish100: "100 mm dish",
  vesselDish150: "150 mm dish",
  vesselPlate6: "6-well plate",
  vesselPlate12: "12-well plate",
  vesselPlate24: "24-well plate",
  vesselPlate48: "48-well plate",
  vesselPlate96: "96-well plate",
  vesselPlate384: "384-well plate",
  vesselPlate1536: "1536-well plate",
  vesselFlaskT25: "T-25 flask",
  vesselFlaskT75: "T-75 flask",
  vesselFlaskT175: "T-175 flask",
  vesselFlaskT225: "T-225 flask",
  vesselOption: "{name} — {area} cm² per well or vessel",
  areaOverride: "Growth area override (cm², optional)",
  areaHint: "Use your vessel maker's figure if it differs from the table.",
  vendorNote:
    "Thermo Fisher's table lists {cells} cells to start with and {medium} mL of growth medium for this vessel (HeLa cells; yours will differ).",
  useVendor: "Use that starting number",
  count: "Number of wells or vessels",
  modeLabel: "Set the seeding density as",
  modePerCm2: "Cells per cm²",
  modePerVessel: "Cells per well or vessel",
  targetPerCm2: "Target density (cells/cm²)",
  targetPerVessel: "Cells per well or vessel",
  concentration: "Suspension concentration (cells/mL)",
  concentrationHint: "From your cell count, for example a hemocytometer.",
  medium: "Total volume per well or vessel (mL)",
  mediumHint: "Cells plus medium once seeded.",
  overage: "Extra mix (%)",
  overageHint: "Covers pipetting loss; 10% is common.",
  results: "Results",
  cellsPerVessel: "Cells per well or vessel",
  suspensionPerVessel: "Suspension per well or vessel (µL)",
  suspensionTotal: "Cell suspension to take (mL)",
  freshMedium: "Fresh medium to add (mL)",
  totalCells: "Total cells needed",
  densityInMix: "Cells/mL in the seeding mix",
  achieved: "Density reached",
  unitPerCm2: "cells/cm²",
  recipeHeading: "Seeding mix",
  colComponent: "Component",
  colVolume: "Volume (mL)",
  rowSuspension: "Cell suspension",
  rowMedium: "Fresh medium",
  rowTotal: "Total seeding mix",
  dispense:
    "Mix gently, then dispense {each} mL into each of {count} wells or vessels.",
  workingHeading: "The working",
  workingCells: "cells per vessel = {density} cells/cm² × {area} cm² = {cells}",
  workingCellsDirect: "cells per vessel = {cells} (entered directly)",
  workingSuspension:
    "suspension = cells needed ÷ concentration = {total} ÷ {conc} = {volume} mL",
  copy: "Copy recipe",
  copied: "Copied!",
  tooDilute:
    "The suspension is too dilute: you would need {volume} mL of it, which is more than the {mix} mL of seeding mix. Concentrate the cells by spinning them down and resuspending in less medium, or seed in a smaller volume.",
  belowMedium:
    "{medium} mL per vessel is below the {min} mL minimum Thermo Fisher suggests for this vessel; the cells may dry out or settle unevenly.",
  aboveMedium:
    "{medium} mL per vessel is above the {max} mL Thermo Fisher suggests for this vessel.",
  errArea: "Enter a growth area greater than 0 cm².",
  errCount: "Enter at least 1 well or vessel.",
  errTarget: "Enter a seeding density greater than 0.",
  errConcentration: "Enter a suspension concentration greater than 0 cells/mL.",
  errMedium: "Enter a volume per well greater than 0 mL.",
  errOverage: "The extra mix must be 0% or more.",
  enter:
    "Enter the number of wells, the density, the suspension concentration and the volume.",
} as const;

type Strings = typeof CELL_SEEDING_CALCULATOR_STRINGS;

const ID = "cs";

const VESSEL_NAMES: Record<VesselId, keyof Strings> = {
  dish35: "vesselDish35",
  dish60: "vesselDish60",
  dish100: "vesselDish100",
  dish150: "vesselDish150",
  plate6: "vesselPlate6",
  plate12: "vesselPlate12",
  plate24: "vesselPlate24",
  plate48: "vesselPlate48",
  plate96: "vesselPlate96",
  plate384: "vesselPlate384",
  plate1536: "vesselPlate1536",
  flaskT25: "vesselFlaskT25",
  flaskT75: "vesselFlaskT75",
  flaskT175: "vesselFlaskT175",
  flaskT225: "vesselFlaskT225",
};

function vesselName(id: VesselId, strings: Strings): string {
  return String(strings[VESSEL_NAMES[id]]);
}

function mlText(fmt: NumberFormatter, value: number): string {
  return fmt(value, { maximumSignificantDigits: 4 });
}

function errorText(code: SeedError, strings: Strings): string {
  const messages: Record<SeedError, string> = {
    area: strings.errArea,
    count: strings.errCount,
    target: strings.errTarget,
    concentration: strings.errConcentration,
    medium: strings.errMedium,
    overage: strings.errOverage,
  };
  return messages[code];
}

function mediumNotes(
  vessel: Vessel,
  mediumMl: number,
  strings: Strings,
  fmt: NumberFormatter
): string[] {
  const [min, max] = vessel.mediumMl;
  const medium = plain(fmt, mediumMl);
  if (mediumMl < min) {
    return [t(strings.belowMedium, { medium, min: plain(fmt, min) })];
  }
  return mediumMl > max
    ? [t(strings.aboveMedium, { medium, max: plain(fmt, max) })]
    : [];
}

function Recipe({
  plan,
  input,
  fmt,
  strings,
}: {
  plan: SeedPlan;
  input: SeedInput;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const head = [strings.colComponent, strings.colVolume];
  const rows = [
    [strings.rowSuspension, mlText(fmt, plan.suspensionMl)],
    [strings.rowMedium, mlText(fmt, plan.freshMediumMl)],
    [strings.rowTotal, mlText(fmt, plan.mixVolumeMl)],
  ];
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.recipeHeading}</h3>
      <DataTable head={head} rows={rows} />
      <p className="text-foreground text-sm">
        {t(strings.dispense, {
          each: mlText(fmt, input.mediumMl),
          count: plain(fmt, input.count),
        })}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={toTsv(head, rows)}
      />
    </div>
  );
}

function Results({
  input,
  vessel,
  fmt,
  strings,
}: {
  input: SeedInput;
  vessel: Vessel;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const outcome = planSeeding(input);
  if ("error" in outcome) {
    return <ErrorNotice>{errorText(outcome.error, strings)}</ErrorNotice>;
  }
  const { plan } = outcome;
  const workingCells =
    input.mode === "perCm2"
      ? t(strings.workingCells, {
          density: sig(fmt, input.target, 4),
          area: plain(fmt, input.areaCm2),
          cells: sig(fmt, plan.cellsPerVessel, 4),
        })
      : t(strings.workingCellsDirect, {
          cells: sig(fmt, plan.cellsPerVessel, 4),
        });
  const notes = mediumNotes(vessel, input.mediumMl, strings, fmt);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.suspensionTotal}
          value={mlText(fmt, plan.suspensionMl)}
        />
        <StatCard
          label={strings.freshMedium}
          value={mlText(fmt, plan.freshMediumMl)}
        />
        <StatCard
          label={strings.cellsPerVessel}
          value={sig(fmt, plan.cellsPerVessel, 4)}
        />
        <StatCard
          label={strings.suspensionPerVessel}
          value={sig(fmt, plan.suspensionPerVesselMl * 1000, 4)}
        />
        <StatCard
          label={strings.totalCells}
          value={sig(fmt, plan.totalCells, 4)}
        />
        <StatCard
          label={`${strings.achieved} (${strings.unitPerCm2})`}
          value={sig(fmt, plan.cellsPerCm2, 4)}
        />
      </StatGrid>
      {plan.tooDilute ? (
        <ErrorNotice>
          {t(strings.tooDilute, {
            volume: mlText(fmt, plan.suspensionMl),
            mix: mlText(fmt, plan.mixVolumeMl),
          })}
        </ErrorNotice>
      ) : (
        <Recipe fmt={fmt} input={input} plan={plan} strings={strings} />
      )}
      {notes.map((note) => (
        <Notice key={note}>{note}</Notice>
      ))}
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p className="font-medium font-sans text-foreground">
          {strings.workingHeading}
        </p>
        <p>{workingCells}</p>
        <p>
          {t(strings.workingSuspension, {
            total: sig(fmt, plan.totalCells, 4),
            conc: sig(fmt, input.concentration, 4),
            volume: mlText(fmt, plan.suspensionMl),
          })}
        </p>
      </div>
    </>
  );
}

export default function CellSeedingCalculatorWidget() {
  const strings = useToolUi(CELL_SEEDING_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [vesselId, setVesselId] = useState<VesselId>("plate24");
  const [areaOverride, setAreaOverride] = useState("");
  const [count, setCount] = useState("12");
  const [mode, setMode] = useState<SeedMode>("perCm2");
  const [target, setTarget] = useState("20000");
  const [concentration, setConcentration] = useState("1e6");
  const [medium, setMedium] = useState("1");
  const [overage, setOverage] = useState("10");

  const vessel = vesselById(vesselId);
  const overrideValue = parseMeasuredNumber(areaOverride);
  const area = overrideValue ?? vessel.areaCm2;
  const countValue = parseMeasuredNumber(count);
  const targetValue = parseMeasuredNumber(target);
  const concValue = parseMeasuredNumber(concentration);
  const mediumValue = parseMeasuredNumber(medium);
  const overageValue = parseMeasuredNumber(overage);
  const ready =
    countValue !== null &&
    targetValue !== null &&
    concValue !== null &&
    mediumValue !== null &&
    overageValue !== null;
  const input: SeedInput | null = ready
    ? {
        areaCm2: area,
        count: countValue,
        mode,
        target: targetValue,
        concentration: concValue,
        mediumMl: mediumValue,
        overagePct: overageValue,
      }
    : null;
  const vesselOptions = VESSELS.map((v) => ({
    value: v.id,
    label: t(strings.vesselOption, {
      name: vesselName(v.id, strings),
      area: plain(fmt, v.areaCm2),
    }),
  }));

  function useVendorNumber() {
    setMode("perVessel");
    setTarget(String(vessel.startingCells));
    setMedium(String(vessel.mediumMl[0]));
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id={`${ID}-vessel`}
          label={strings.vesselLabel}
          onChange={setVesselId}
          options={vesselOptions}
          value={vesselId}
        />
        <TextField
          hint={strings.areaHint}
          id={`${ID}-area`}
          label={strings.areaOverride}
          numeric
          onChange={setAreaOverride}
          value={areaOverride}
        />
      </div>
      <div className="flex flex-wrap items-center gap-3 text-muted-foreground text-sm">
        <span>
          {t(strings.vendorNote, {
            cells: sig(fmt, vessel.startingCells, 3),
            medium:
              vessel.mediumMl[0] === vessel.mediumMl[1]
                ? plain(fmt, vessel.mediumMl[0])
                : `${plain(fmt, vessel.mediumMl[0])}–${plain(fmt, vessel.mediumMl[1])}`,
          })}
        </span>
        <button
          className={BUTTON_SECONDARY}
          onClick={useVendorNumber}
          type="button"
        >
          {strings.useVendor}
        </button>
      </div>
      <PillGroup
        label={strings.modeLabel}
        name={`${ID}-mode`}
        onChange={setMode}
        options={[
          { value: "perCm2", label: strings.modePerCm2 },
          { value: "perVessel", label: strings.modePerVessel },
        ]}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <TextField
          id={`${ID}-count`}
          label={strings.count}
          numeric
          onChange={setCount}
          value={count}
        />
        <TextField
          id={`${ID}-target`}
          label={
            mode === "perCm2" ? strings.targetPerCm2 : strings.targetPerVessel
          }
          numeric
          onChange={setTarget}
          value={target}
        />
        <TextField
          hint={strings.concentrationHint}
          id={`${ID}-conc`}
          label={strings.concentration}
          numeric
          onChange={setConcentration}
          value={concentration}
        />
        <TextField
          hint={strings.mediumHint}
          id={`${ID}-medium`}
          label={strings.medium}
          numeric
          onChange={setMedium}
          value={medium}
        />
        <TextField
          hint={strings.overageHint}
          id={`${ID}-overage`}
          label={strings.overage}
          numeric
          onChange={setOverage}
          value={overage}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {input ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <Results
              fmt={fmt}
              input={input}
              strings={strings}
              vessel={vessel}
            />
          </div>
        ) : (
          <Notice>{strings.enter}</Notice>
        )}
      </section>
    </>
  );
}
